import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { map, switchMap, take } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { BdFeaturePermissionsStore } from '../store/features.store';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';
import { BdAdminPermissionService } from '../../admin/core/admin-permissions.service';
import { BdAccountScopedPermissionCache } from '../../cache/account-scoped-permission-cache';

/**
 * Session-storage key holding the feature permissions of the signed-in account.
 */
export const BD_FEATURE_PERMISSION_STORAGE_BUCKET =
  'bd.config.BD_FEATURE_PERMISSIONS';

@Injectable({
  providedIn: 'root',
})
export class BdFeaturePermissionService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _environmentService = inject(BdAppEnvironmentService);
  private readonly _authService = inject(BdAuthenticationService);
  private readonly _adminPermissionService = inject(BdAdminPermissionService);
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _store = new BdFeaturePermissionsStore(
    this._environmentService.load(ApiEndpointKey.BD_PERMISSION_RESOURCE_URL),
  );

  private readonly _cache = new BdAccountScopedPermissionCache<
    `${string}:${string}`[]
  >(BD_FEATURE_PERMISSION_STORAGE_BUCKET, this._isBrowser);

  /**
   * Returns the union of feature permissions and admin permissions.
   *
   * Feature and admin permissions share the `${string}:${string}` shape and
   * never collide (admin entries are namespaced `ADMIN:*`). Every consumer of
   * feature permissions also needs admin permissions for visibility checks
   * (menu items, tabs, route guards), so the merge happens here once instead
   * of being threaded through every call site.
   *
   * `force` deliberately applies to the feature permissions only. Callers force
   * a refresh so a newly granted feature permission shows up without opening a
   * new tab, and `GET /feature-permissions/` answers 200 for everyone. Admin
   * permissions are read from the account-scoped session cache instead: they
   * change only for staff, and their endpoint answers 404 for everyone else,
   * which the browser logs as an error on every page load (BDA-6069).
   */
  public getPermissions(
    force: boolean = false,
  ): Observable<`${string}:${string}`[]> {
    return combineLatest([
      this._loadFeaturePermissions(force),
      this._adminPermissionService.getPermissions(),
    ]).pipe(map(([feature, admin]) => [...feature, ...admin]));
  }

  public containsPermission(
    permissionsToCheck: `${string}:${string}`[],
  ): Observable<boolean> {
    return this.getPermissions().pipe(
      map((allPermissions) =>
        permissionsToCheck.every(
          (singlePermission) => allPermissions.indexOf(singlePermission) !== -1,
        ),
      ),
    );
  }

  /**
   * Waits for the signed-in account before touching the cache: the account is
   * what makes a cached entry valid, and it is reported one synchronous step
   * after consumers start asking (see `BdAccountScopedPermissionCache`).
   */
  private _loadFeaturePermissions(
    force: boolean,
  ): Observable<`${string}:${string}`[]> {
    return this._authService.accountInfo$.pipe(
      take(1),
      switchMap((account) =>
        this._cache.resolve(account?.homeAccountId ?? null, force, () =>
          this._fetchAllPermissions(),
        ),
      ),
    );
  }

  private _fetchAllPermissions(): Observable<`${string}:${string}`[]> {
    const url = this._store.findAllPermissions();
    return this._httpClient.get<`${string}:${string}`[]>(url);
  }
}
